import { useEffect, useState } from "react";

export const COOKIE_CONSENT_CHANGED_EVENT = "cookieConsentChanged";
// Dispatched by anything (e.g. the footer's "Cookie settings" link) that wants to
// reopen the consent panel after the initial banner has already been dismissed —
// otherwise a visitor's only way to revisit their choice is clearing browser storage.
export const OPEN_COOKIE_SETTINGS_EVENT = "openCookieSettings";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

function readAnalyticsConsent(): boolean {
  try {
    const saved = localStorage.getItem("cookiePreferences");
    if (!saved) return false;
    return Boolean((JSON.parse(saved) as Partial<CookiePreferences>).analytics);
  } catch {
    return false;
  }
}

/**
 * Tracks whether the visitor has consented to analytics cookies, so
 * components that only make sense under that consent (Vercel Analytics,
 * Speed Insights, etc.) can mount/unmount live as the choice changes —
 * rendering them unconditionally at the app root would collect telemetry
 * before any consent decision exists, defeating the cookie banner's
 * "Essential only" option.
 */
export function useCookieConsent(): { analytics: boolean } {
  const [analytics, setAnalytics] = useState(readAnalyticsConsent);

  useEffect(() => {
    const refresh = () => setAnalytics(readAnalyticsConsent());
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return { analytics };
}
